// Angular
import { inject, Injectable, signal } from '@angular/core';
import { ResolveFn } from '@angular/router';

// RxJS
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Component Library
import { FsxDialogService } from 'fsx-component-library';

// Data Contracts
import { ClerkReviewFiling, ClerkReviewFilingConfiguration } from '@data-contracts';

// Local
import { ClerkReviewApiService } from '@app/services/api/clerk-review/clerk-review-api.service';
import {
  getResourceLockUserMessage,
  isResourceLocked,
} from '@app/shared/utils/resource-lock-error.utils';

/**
 * Holds the dedupe guard for the clerk review resolver error dialog so that both
 * resolvers failing during the same navigation only surface one modal.
 *
 * Kept as an injectable (rather than module-level mutable state) so it
 * participates in DI and resets naturally per TestBed, avoiding order-dependent
 * test flakiness. `reset()` is called at the start of every resolver run — both
 * resolvers for a navigation run before either HTTP call settles, so the first
 * failure still shows exactly one dialog and the second is suppressed, while the
 * next navigation always starts from a clean state (it can never latch on
 * permanently if a dialog is torn down without emitting a close event).
 */
@Injectable({ providedIn: 'root' })
export class ClerkReviewErrorDialogState {
  private readonly active = signal(false);

  isActive(): boolean {
    return this.active();
  }

  markActive(): void {
    this.active.set(true);
  }

  reset(): void {
    this.active.set(false);
  }
}

/**
 * Centralized error handling for the clerk review resolvers.
 *
 * Whatever the failure, we notify the user with a modal and cancel the
 * navigation by rethrowing WITHOUT redirecting — this leaves them on the clerk
 * review list rather than sending them home. Lock errors (HTTP 423) get a
 * tailored "locked by another user" message (including the locking user's name
 * when the API returns it); all other errors get a generic "an error has
 * occurred" message.
 */
function handleClerkReviewResolverError(
  err: unknown,
  errorMessage: string,
  deps: {
    dialog: FsxDialogService;
    dialogState: ClerkReviewErrorDialogState;
  },
): never {
  console.error(errorMessage, err);

  if (!deps.dialogState.isActive()) {
    deps.dialogState.markActive();

    const isLocked = isResourceLocked(err);

    const title = isLocked ? 'Filing Locked' : 'Error';
    const message = isLocked
      ? getResourceLockUserMessage(err)
      : 'An error has occurred while opening this filing. Please try again.';

    // Single-button informational dialog (Ok/Cancel previously did the same thing).
    // openDialog always renders its secondary button and only adds a primary when
    // an actionButtonLabel is passed — so pass none, then label the secondary "Ok".
    const dialogRef = deps.dialog.openDialog(title, message, undefined, {
      titleStyle: { color: 'black' },
    });
    dialogRef.dialogRef.componentInstance.closeButtonLabel = 'Ok';
  }

  // Rethrow without navigating: cancels the route activation and keeps the
  // user on the clerk review list.
  throw err;
}

/**
 * Resolver to get clerk review configuration for a given filing unique ID before activating the clerk review route.
 */
export const clerkReviewResolver: ResolveFn<Observable<ClerkReviewFilingConfiguration>> = (route) => {
  
    const filingId = route.paramMap.get('filingUniqueId');
    if (!filingId) {
      console.error('Filing unique ID is missing in the route parameters.');
      return null;
    }

  /**
   * The clerk review api service.
   */
  const clerkReviewApiService = inject(ClerkReviewApiService);
  /**
   * The dialog service, used to notify the user when the filing can't be opened.
   */
  const dialog = inject(FsxDialogService);
  /**
   * Dedupe guard so paired resolvers don't stack duplicate error dialogs.
   */
  const dialogState = inject(ClerkReviewErrorDialogState);

  dialogState.reset();

  return clerkReviewApiService.getClerkReviewConfiguration(filingId).pipe(
    catchError(err =>
      handleClerkReviewResolverError(
        err,
        'An error occurred while fetching clerk review configuration.',
        { dialog, dialogState },
      ),
    ),
  );
}

/**
 * Resolver to get clerk review data for a given filing unique ID before activating the clerk review route.
 */
export const clerkReviewDataResolver: ResolveFn<Observable<ClerkReviewFiling>> = (route) => {
  
    const filingId = route.paramMap.get('filingUniqueId');
    if (!filingId) {
      console.error('Filing unique ID is missing in the route parameters.');
      return null;
    }

  /**
   * The clerk review api service.
   */
  const clerkReviewApiService = inject(ClerkReviewApiService);
  /**
   * The dialog service, used to notify the user when the filing can't be opened.
   */
  const dialog = inject(FsxDialogService);
  /**
   * Dedupe guard so paired resolvers don't stack duplicate error dialogs.
   */
  const dialogState = inject(ClerkReviewErrorDialogState);

  dialogState.reset();

  return clerkReviewApiService.getClerkReviewData(filingId).pipe(
    catchError(err =>
      handleClerkReviewResolverError(
        err,
        'An error occurred while fetching clerk review data.',
        { dialog, dialogState },
      ),
    ),
  );
}
